import { supabase } from './supabase'

export type GeocodeAddress = {
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}

export type GeocodeResult = {
  latitude: number
  longitude: number
  provider?: string
  matched_address?: string | null
  accuracy?: string | null
}

/** Precise geocode only — rejects city/zip centroids. */
export async function geocodeAddress(addr: GeocodeAddress): Promise<GeocodeResult | null> {
  if (!addr.address?.trim()) return null
  try {
    const res = await fetch('/api/geocode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        address: addr.address.trim(),
        city: (addr.city || '').trim(),
        state: (addr.state || '').trim(),
        zip: (addr.zip_code || '').trim(),
      }),
    })
    const json = await res.json()
    if (!res.ok || !json?.success) return null
    if (json.accuracy && json.accuracy !== 'rooftop' && json.accuracy !== 'house') return null
    return {
      latitude: Number(json.latitude),
      longitude: Number(json.longitude),
      provider: json.provider || null,
      matched_address: json.matched_address || null,
      accuracy: json.accuracy || 'rooftop',
    }
  } catch {
    return null
  }
}

export async function geocodeAndSaveProperty(
  propertyId: string,
  addr: GeocodeAddress
): Promise<GeocodeResult | null> {
  const coords = await geocodeAddress(addr)
  if (!coords) return null
  const { error } = await supabase
    .from('properties')
    .update({
      latitude: coords.latitude,
      longitude: coords.longitude,
      geocode_source: coords.provider || null,
      geocode_accuracy: coords.accuracy || null,
      geocode_matched_address: coords.matched_address || null,
    })
    .eq('id', propertyId)
  if (error) return null
  return coords
}

export async function savePropertyCoordinates(
  propertyId: string,
  latitude: number,
  longitude: number,
  matchedAddress?: string | null
): Promise<void> {
  const { error } = await supabase
    .from('properties')
    .update({
      latitude,
      longitude,
      geocode_source: 'manual',
      geocode_accuracy: 'manual',
      geocode_matched_address: matchedAddress || null,
    })
    .eq('id', propertyId)
  if (error) throw error
}

export type PropertyGeocodeTarget = {
  id: string
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
  latitude?: number | null
  longitude?: number | null
}

export async function geocodeMissingProperties(
  properties: PropertyGeocodeTarget[]
): Promise<Map<string, { latitude: number; longitude: number }>> {
  const result = new Map<string, { latitude: number; longitude: number }>()
  const pending = new Map<string, PropertyGeocodeTarget>()

  for (const p of properties) {
    if (!p?.id) continue
    if (
      p.latitude != null &&
      p.longitude != null &&
      Number.isFinite(p.latitude) &&
      Number.isFinite(p.longitude)
    ) {
      result.set(p.id, { latitude: p.latitude, longitude: p.longitude })
      continue
    }
    if (!p.address?.trim() || pending.has(p.id)) continue
    pending.set(p.id, p)
  }

  for (const p of Array.from(pending.values())) {
    const coords = await geocodeAndSaveProperty(p.id, p)
    if (coords) result.set(p.id, coords)
  }

  return result
}
